import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Container, Row, Spinner } from 'react-bootstrap';
import * as icons from 'react-bootstrap-icons';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckoutAddPaymentStatus } from '../../../redux/auth-reducer/auth-service';
import {
  clearCheckoutIntent,
  clearCheckoutReturn,
  readApiBody,
  readCheckoutSuccessReturn,
} from '../../../utils/helper/checkout';

/**
 * Landing page Stripe redirects to after checkout.
 *
 * This page does not grant the subscription - that is done from the Stripe webhook. The call
 * below confirms the specific session named in the URL and reconciles it, so it is a safety net
 * for a webhook that has not arrived rather than a second source of truth. Reloading the page
 * therefore changes nothing; it used to add another subscription period every time.
 *
 * It serves both purchases. The server branches on the checkout mode, so a single-auction
 * purchase lands here too and comes back with a message naming the auction it unlocked - which
 * is why every message below is taken from the response rather than written here.
 *
 * WEB-P1-116: it used to render the green tick and "Payment Successfully" unconditionally, and
 * forward to the dashboard five seconds later whatever the server said - it computed the
 * outcome and then threw it away. Only a confirmed payment leaves on its own now; the other
 * two states stay put and offer a next step, because walking someone to a dashboard that will
 * greet them with "you are not subscribed" is how "charged and never told" looked from inside.
 *
 * #238 AC8: a checkout that asked for it (an auction pass, from the Auctions list or Auction
 * Details) leaves for the page it was started from instead of the dashboard, where the auction now
 * reads as unlocked. A subscription still goes to the dashboard.
 */

const STATE = {
  CONFIRMING: 'confirming',
  CONFIRMED: 'confirmed',
  UNCONFIRMED: 'unconfirmed',
  ERROR: 'error',
};

const REDIRECT_DELAY_MS = 5000;

export const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const role = localStorage.getItem('role');
  // Read while rendering, before the effect below clears it.
  const [successReturn] = useState(readCheckoutSuccessReturn);
  const destination =
    successReturn || (role === 'Manager' ? '/SuperAdmin/SuperDashboard' : '/Player/Player_dashboard');

  const [state, setState] = useState(STATE.CONFIRMING);
  const [message, setMessage] = useState('');
  const redirectTimer = useRef(null);

  /**
   * Asks the server what Stripe says about this session.
   *
   * Safe to call again: the server-side confirm is a reconcile, not a grant - the subscription
   * comes from the webhook - so a second call changes nothing. Stripe/GetPaymentStatus is
   * listed in apiClient's NON_IDEMPOTENT_GETS, which suppresses only the *automatic* retry;
   * this deliberate, user-pressed one is the intended affordance.
   */
  const confirmPayment = async () => {
    setState(STATE.CONFIRMING);

    const body = readApiBody(await CheckoutAddPaymentStatus(sessionId));

    if (body?.status === true) {
      setState(STATE.CONFIRMED);
      setMessage(body.message || 'Your payment has been confirmed.');
      return;
    }

    if (body) {
      // The server answered and said no. Its message is the only accurate thing to show.
      setState(STATE.UNCONFIRMED);
      setMessage(body.message || 'Stripe has not reported this payment as complete yet.');
      return;
    }

    setState(STATE.ERROR);
    setMessage('We could not reach the server to confirm your payment. Your payment may still have gone through.');
  };

  useEffect(() => {
    // Whatever the answer, this purchase is over: nothing should resume it at the next sign-in.
    clearCheckoutIntent();
    // Nor should a later Back on another Stripe form be sent to where this one started.
    clearCheckoutReturn();
    confirmPayment();
    return () => clearTimeout(redirectTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (state !== STATE.CONFIRMED || (role !== 'Player' && role !== 'Manager')) return;
    redirectTimer.current = setTimeout(() => navigate(destination), REDIRECT_DELAY_MS);
    return () => clearTimeout(redirectTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const isFailure = state === STATE.UNCONFIRMED || state === STATE.ERROR;

  return (
    <>
      <section className="payment-success">
        <Container>
          <Row className="align-items-center justify-content-center">
            <Col md={6} className="text-center">
              {state === STATE.CONFIRMING && (
                <>
                  <Spinner animation="border" variant="primary" />
                  <h2 className="mt-3">Confirming your payment</h2>
                  <p>This only takes a moment.</p>
                </>
              )}

              {state === STATE.CONFIRMED && (
                <>
                  <icons.Check2Circle className="payment-check" />
                  <h2>Payment confirmed</h2>
                  <p>{message}</p>
                  <p>Thanks for being with us.</p>
                  {(role === 'Player' || role === 'Manager') && (
                    <p className="text-muted small">
                      {successReturn ? 'Taking you back...' : 'Taking you to your account...'}
                    </p>
                  )}
                </>
              )}

              {state === STATE.UNCONFIRMED && (
                <>
                  <icons.ExclamationTriangle className="payment-check-cancel" />
                  <h2>We could not confirm your payment</h2>
                  <p>{message}</p>
                  <p>If your card was charged this usually settles within a few minutes.</p>
                </>
              )}

              {state === STATE.ERROR && (
                <>
                  <icons.XCircle className="payment-check-cancel" />
                  <h2>We could not confirm your payment</h2>
                  <p>{message}</p>
                </>
              )}

              {isFailure && (
                <>
                  {sessionId && (
                    <p className="text-muted small">Reference: {sessionId}</p>
                  )}
                  <div className="d-flex gap-2 justify-content-center flex-wrap">
                    <Button onClick={confirmPayment}>
                      <icons.ArrowRepeat /> Check again
                    </Button>
                    <Button
                      variant="outline-secondary"
                      onClick={() =>
                        navigate(
                          role === 'Player' || role === 'Manager' ? destination : '/Home'
                        )
                      }
                    >
                      Go to my account
                    </Button>
                    <Link className="btn btn-link" to="/ContactUs">
                      Contact support
                    </Link>
                  </div>
                </>
              )}
            </Col>
            <Col md={6}>
              <img
                src={isFailure ? '../image/payment-cancel.png' : '../image/succes-icon.png'}
                className="img-fluid"
                alt=""
              />
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};
